import React from 'react';
import { format, startOfMonth, endOfMonth } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Layout } from '../components/layout/Layout';
import { KPICard } from '../components/reports/KPICard';
import { TripTrendChart } from '../components/charts/TripTrendChart';
import { ProfessionalVsPersonalChart } from '../components/charts/ProfessionalVsPersonalChart';
import { TripTable } from '../components/trips/TripTable';
import { useMonthlySummary, useDailySummary } from '../hooks/useReports';
import { useTrips } from '../hooks/useTrips';

export const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const currentMonth = format(new Date(), 'yyyy-MM');
  const monthStart = format(startOfMonth(new Date()), 'yyyy-MM-dd');
  const monthEnd = format(endOfMonth(new Date()), 'yyyy-MM-dd');

  const { data: monthlySummary, isLoading: summaryLoading } = useMonthlySummary(currentMonth);
  const { data: dailyData } = useDailySummary(monthStart, monthEnd);
  const { data: recentTrips, isLoading: tripsLoading } = useTrips({ limit: 5, page: 1 });

  if (summaryLoading) {
    return (
      <Layout>
        <div className="space-y-6">
          <h1 className="text-2xl font-bold text-gray-900">{t('dashboard.title')}</h1>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg animate-pulse" />
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  if (!monthlySummary) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-xl font-semibold text-gray-900">{t('dashboard.noData')}</h2>
        </div>
      </Layout>
    );
  }

  const qualificationRate =
    monthlySummary.total_trips > 0
      ? ((monthlySummary.professional_count + monthlySummary.personal_count) /
          monthlySummary.total_trips) *
        100
      : 0;

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1">
          <h1 className="text-2xl font-bold text-gray-900">{t('dashboard.title')}</h1>
          <p className="text-sm text-gray-500">
            {t('dashboard.lastUpdated', { date: format(new Date(), 'MMM dd, yyyy HH:mm') })}
          </p>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <KPICard
            title={t('dashboard.kpi.totalKm')}
            value={monthlySummary.total_km}
            unit="km"
            icon="🚗"
            subtitle={t('dashboard.kpi.thisMonth')}
          />
          <KPICard
            title={t('dashboard.kpi.professionalKm')}
            value={monthlySummary.professional_km}
            unit="km"
            color="green"
            icon="💼"
            subtitle={t('dashboard.kpi.thisMonth')}
          />
          <KPICard
            title={t('dashboard.kpi.personalKm')}
            value={monthlySummary.personal_km}
            unit="km"
            color="orange"
            icon="🏠"
            subtitle={t('dashboard.kpi.thisMonth')}
          />
          <KPICard
            title={t('dashboard.kpi.qualificationRate')}
            value={qualificationRate}
            unit="%"
            color="blue"
            icon="✓"
            subtitle={t('dashboard.kpi.trips', {
              qualified: monthlySummary.professional_count + monthlySummary.personal_count,
              total: monthlySummary.total_trips,
            })}
          />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4">{t('dashboard.charts.tripTrends')}</h2>
            {dailyData.length > 0 ? (
              <TripTrendChart data={dailyData} />
            ) : (
              <div className="h-72 flex items-center justify-center text-gray-500">
                {t('dashboard.charts.noData')}
              </div>
            )}
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold mb-4">
              {t('dashboard.charts.professionalVsPersonal')}
            </h2>
            <ProfessionalVsPersonalChart data={monthlySummary} />
          </div>
        </div>

        {/* Recent Trips */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200 flex justify-between items-center">
            <h2 className="text-lg font-semibold">{t('dashboard.recentTrips.title')}</h2>
            <a
              href="/trips"
              className="text-sm font-medium text-primary-600 hover:text-primary-800"
            >
              {t('dashboard.recentTrips.viewAll')}
            </a>
          </div>
          <div className="p-6">
            <TripTable trips={recentTrips} isLoading={tripsLoading} />
          </div>
        </div>

        {/* Alert Banner */}
        {monthlySummary.pending_count > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex flex-col sm:flex-row sm:items-start gap-3">
              <div className="flex items-start gap-3 flex-1">
                <span className="text-2xl shrink-0">⚠️</span>
                <div>
                  <h3 className="text-sm font-medium text-yellow-800">
                    {t('dashboard.alert.pendingTrips', { count: monthlySummary.pending_count })}
                  </h3>
                  <p className="mt-1 text-sm text-yellow-700">
                    {t('dashboard.alert.pendingMessage')}
                  </p>
                </div>
              </div>
              <a
                href="/trips?status=pending"
                className="shrink-0 self-start sm:self-auto px-4 py-2 bg-yellow-600 text-white text-sm font-medium rounded-md hover:bg-yellow-700"
              >
                {t('dashboard.alert.reviewNow')}
              </a>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};
